import React, { useState, useEffect } from 'react';
import { Trophy, Gift, X, ChevronRight, Check, Star } from 'lucide-react';

const REWARD_TYPES = [
  { key: 'weekly_rate', label: 'Pro Woche' },
  { key: 'streak', label: 'Streak' },
  { key: 'total_completions', label: 'Insgesamt' },
];

function suggestNextThreshold(ruleType, oldThreshold) {
  if (ruleType === 'total_completions') return oldThreshold + Math.ceil(oldThreshold * 0.5);
  if (ruleType === 'streak') return oldThreshold + 7;
  return oldThreshold; // weekly_rate resets each week → same threshold
}

function getRuleLabel(rule) {
  if (!rule) return '';
  if (rule.ruleType === 'streak') return `${rule.threshold} Tage Streak erreicht`;
  if (rule.ruleType === 'weekly_rate') return `${rule.threshold}× diese Woche`;
  return `${rule.threshold}× insgesamt`;
}

const CONFETTI_COLORS = ['#a855f7', '#fbbf24', '#34d399', '#60a5fa', '#f472b6', '#fb923c', '#e879f9'];

export default function RewardCelebrationModal({ habit, triggeredRule, ruleIndex, onClose, onSaveNextReward }) {
  const [phase, setPhase] = useState('celebration');
  const [ruleType, setRuleType] = useState(triggeredRule?.ruleType || 'streak');
  const [threshold, setThreshold] = useState(() =>
    suggestNextThreshold(triggeredRule?.ruleType, triggeredRule?.threshold || 7)
  );
  const [rewardText, setRewardText] = useState('');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 250);
  };

  const handleSave = () => {
    if (!rewardText.trim()) { handleClose(); return; }
    onSaveNextReward(habit, ruleIndex, { ruleType, threshold, rewardText: rewardText.trim() });
  };

  return (
    <>
      <style>{`
        @keyframes confetti-rise {
          0%   { transform: translateY(0) rotate(0deg) scale(1); opacity: 1; }
          80%  { opacity: 0.7; }
          100% { transform: translateY(-180px) rotate(520deg) scale(0); opacity: 0; }
        }
        @keyframes trophy-pop {
          0%   { transform: scale(0) rotate(-10deg); opacity: 0; }
          55%  { transform: scale(1.18) rotate(4deg); opacity: 1; }
          75%  { transform: scale(0.95) rotate(-2deg); }
          100% { transform: scale(1) rotate(0deg); opacity: 1; }
        }
        @keyframes reward-slide-up {
          from { transform: translateY(24px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        @keyframes shimmer-bg {
          0%   { background-position: 200% center; }
          100% { background-position: -200% center; }
        }
        .trophy-pop  { animation: trophy-pop 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards; }
        .reward-slide { animation: reward-slide-up 0.45s ease-out forwards; }
        .shimmer-text {
          background: linear-gradient(90deg, #fff 30%, #fde68a 50%, #fff 70%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: shimmer-bg 2s linear infinite;
        }
      `}</style>

      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{
          backgroundColor: `rgba(0,0,0,${visible ? 0.72 : 0})`,
          backdropFilter: visible ? 'blur(6px)' : 'none',
          transition: 'background-color 0.25s, backdrop-filter 0.25s'
        }}
        onClick={handleClose}
      >
        {/* Modal */}
        <div
          className="w-full max-w-sm bg-white rounded-3xl overflow-hidden shadow-2xl"
          style={{
            transform: visible ? 'scale(1) translateY(0)' : 'scale(0.85) translateY(40px)',
            opacity: visible ? 1 : 0,
            transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.25s ease'
          }}
          onClick={e => e.stopPropagation()}
        >
          {phase === 'celebration' ? (
            <>
              {/* Celebration Header */}
              <div className="relative overflow-hidden bg-gradient-to-br from-purple-600 via-violet-600 to-indigo-700 px-6 pt-8 pb-12 text-center">
                {/* Confetti particles */}
                {CONFETTI_COLORS.map((color, i) => (
                  <div
                    key={i}
                    className="absolute rounded-full"
                    style={{
                      width: i % 3 === 0 ? 10 : 7,
                      height: i % 3 === 0 ? 10 : 7,
                      backgroundColor: color,
                      left: `${8 + i * 13}%`,
                      bottom: '18%',
                      animation: `confetti-rise ${1.0 + i * 0.12}s ease-out ${i * 0.08}s forwards`
                    }}
                  />
                ))}
                {/* Second row of confetti */}
                {CONFETTI_COLORS.map((color, i) => (
                  <div
                    key={`b${i}`}
                    className="absolute"
                    style={{
                      width: 6, height: 6,
                      borderRadius: i % 2 === 0 ? '50%' : '2px',
                      backgroundColor: CONFETTI_COLORS[(i + 3) % CONFETTI_COLORS.length],
                      left: `${4 + i * 14}%`,
                      bottom: '25%',
                      animation: `confetti-rise ${1.3 + i * 0.1}s ease-out ${0.15 + i * 0.07}s forwards`
                    }}
                  />
                ))}

                {/* Trophy icon */}
                <div className="trophy-pop w-24 h-24 bg-white/15 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4 ring-2 ring-white/25">
                  <Trophy className="w-12 h-12 text-yellow-300 drop-shadow-lg" />
                </div>

                {/* Text */}
                <h2 className="shimmer-text text-2xl font-black tracking-tight mb-1">
                  Belohnung verdient!
                </h2>
                <p className="text-white/65 text-sm font-medium">{habit?.title}</p>
              </div>

              {/* Content */}
              <div className="reward-slide px-5 py-5 space-y-4">
                {/* Reward card */}
                <div className="relative bg-gradient-to-br from-purple-50 to-violet-50 border border-purple-100 rounded-2xl p-4 text-center overflow-hidden">
                  <div className="absolute top-2 right-2 opacity-10">
                    <Star className="w-8 h-8 text-purple-400" />
                  </div>
                  <Gift className="w-7 h-7 text-purple-500 mx-auto mb-2" />
                  <p className="text-base font-bold text-purple-800 leading-snug">
                    {triggeredRule?.rewardText}
                  </p>
                  <p className="text-xs text-purple-400 mt-1.5 font-medium uppercase tracking-wide">
                    {getRuleLabel(triggeredRule)}
                  </p>
                </div>

                {/* Actions */}
                <div className="grid grid-cols-2 gap-2.5">
                  <button
                    onClick={handleClose}
                    className="py-3 bg-slate-100 text-slate-600 text-sm font-semibold rounded-2xl hover:bg-slate-200 active:scale-95 transition-all"
                    data-testid="reward-modal-close"
                  >
                    Schließen
                  </button>
                  <button
                    onClick={() => setPhase('next-reward')}
                    className="py-3 bg-purple-600 text-white text-sm font-bold rounded-2xl hover:bg-purple-700 active:scale-95 transition-all flex items-center justify-center gap-1.5 shadow-lg shadow-purple-200"
                    data-testid="reward-modal-next"
                  >
                    Nächstes Ziel
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Next Reward Header */}
              <div className="px-5 pt-5 pb-4 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-purple-500 mb-0.5">
                    Nächster Meilenstein
                  </p>
                  <h3 className="text-base font-bold text-slate-800">Neue Belohnung setzen</h3>
                </div>
                <button
                  onClick={handleClose}
                  className="p-1.5 text-slate-400 hover:text-slate-600 rounded-xl hover:bg-slate-100 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="p-5 space-y-4">
                {/* Rule type pills */}
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-2">Art der Belohnung</p>
                  <div className="flex gap-2">
                    {REWARD_TYPES.map(t => (
                      <button
                        key={t.key}
                        onClick={() => {
                          setRuleType(t.key);
                          setThreshold(suggestNextThreshold(t.key, triggeredRule?.threshold || 7));
                        }}
                        className={`flex-1 py-2.5 rounded-xl text-xs font-bold transition-all ${
                          ruleType === t.key
                            ? 'bg-purple-600 text-white shadow-md shadow-purple-200 scale-[1.02]'
                            : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                        }`}
                        data-testid={`next-reward-type-${t.key}`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Threshold */}
                <div className="bg-slate-50 rounded-2xl p-4 flex items-center gap-3">
                  <span className="text-sm text-slate-400 font-medium flex-shrink-0">Wenn</span>
                  <input
                    type="number"
                    min={1}
                    value={threshold}
                    onChange={e => setThreshold(Number(e.target.value))}
                    className="w-16 px-2 py-2 border border-slate-200 bg-white rounded-xl text-center font-bold text-lg text-slate-800 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                    data-testid="next-reward-threshold"
                  />
                  <span className="text-sm text-slate-500">
                    {ruleType === 'weekly_rate' ? 'x diese Woche'
                      : ruleType === 'streak' ? 'Tage am Stück'
                      : 'x insgesamt'}
                  </span>
                </div>

                {/* Reward text */}
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-1.5">Deine Belohnung</p>
                  <input
                    type="text"
                    value={rewardText}
                    onChange={e => setRewardText(e.target.value)}
                    placeholder="z.B. Lieblingsfilm, Essen gehen, Massage..."
                    className="w-full px-4 py-3 border border-slate-200 rounded-2xl text-sm text-slate-800 placeholder-slate-300 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                    data-testid="next-reward-text"
                    autoFocus
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="px-5 pb-5 space-y-2">
                <button
                  onClick={handleSave}
                  disabled={!rewardText.trim()}
                  className={`w-full py-3.5 text-sm font-bold rounded-2xl transition-all flex items-center justify-center gap-2 ${
                    rewardText.trim()
                      ? 'bg-purple-600 hover:bg-purple-700 text-white shadow-lg shadow-purple-200 active:scale-[0.98]'
                      : 'bg-slate-100 text-slate-300 cursor-not-allowed'
                  }`}
                  data-testid="next-reward-save"
                >
                  <Check className="w-4 h-4" />
                  Belohnung speichern
                </button>
                <button
                  onClick={handleClose}
                  className="w-full py-2 text-xs text-slate-400 hover:text-slate-500 transition-colors"
                  data-testid="next-reward-skip"
                >
                  Überspringen
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
